'use client';

import React, { useState, useEffect } from 'react';
import { LogOut, Monitor, Columns } from 'lucide-react';
import { trackViewToggle } from '../utils/telemetry';

// Generate user initials from display name
const getUserInitials = (displayName) => {
  if (!displayName) return '?';
  const names = displayName.trim().split(' ').filter(Boolean);
  if (names.length >= 2) {
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }
  return names[0]?.[0]?.toUpperCase() || '?';
};

// Generate consistent color from string (for initials background)
const getInitialsColor = (str) => {
  const colors = [
    'bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-red-600',
    'bg-orange-500', 'bg-teal-600', 'bg-indigo-600', 'bg-pink-600'
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// localStorage key for view preference
export const VIEW_PREFERENCE_KEY = 'dashboardViewPreference';
export const VIEW_CLASSIC = 'classic';
export const VIEW_SPLIT = 'split';

// User Account Menu Component (Microsoft 365 style) with view toggle
const UserAccountMenu = ({
  userDisplayName,
  userEmail,
  userPhoto,
  handleLogout,
  currentView,
  onViewChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const initials = getUserInitials(userDisplayName);
  const initialsColor = getInitialsColor(userEmail || userDisplayName || '');

  const handleViewSwitch = (newView) => {
    const previousView = currentView;
    if (typeof window !== 'undefined') {
      localStorage.setItem(VIEW_PREFERENCE_KEY, newView);
    }
    if (onViewChange) {
      onViewChange(newView);
    }
    setIsOpen(false);
    // Track view toggle after state update (fire and forget)
    if (previousView !== newView) {
      trackViewToggle(userEmail, previousView, newView);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden hover:ring-2 hover:ring-white/50 transition-all"
        title={userDisplayName}
      >
        {userPhoto ? (
          <img
            src={userPhoto}
            alt={userDisplayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${initialsColor} text-white text-sm font-semibold`}>
            {initials}
          </div>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden">
            {/* User info header */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  {userPhoto ? (
                    <img
                      src={userPhoto}
                      alt={userDisplayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${initialsColor} text-white text-lg font-semibold`}>
                      {initials}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">{userDisplayName}</p>
                  <p className="text-sm text-gray-500 truncate">{userEmail}</p>
                </div>
              </div>
            </div>

            {/* View Toggle Section */}
            <div className="p-2 border-b border-gray-200">
              <p className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Dashboard View</p>
              <button
                onClick={() => handleViewSwitch(VIEW_CLASSIC)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  currentView === VIEW_CLASSIC
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Monitor className={`w-4 h-4 ${currentView === VIEW_CLASSIC ? 'text-green-600' : 'text-gray-500'}`} />
                Classic View
                {currentView === VIEW_CLASSIC && (
                  <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                )}
              </button>
              <button
                onClick={() => handleViewSwitch(VIEW_SPLIT)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  currentView === VIEW_SPLIT
                    ? 'bg-cyan-50 text-cyan-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Columns className={`w-4 h-4 ${currentView === VIEW_SPLIT ? 'text-cyan-600' : 'text-gray-500'}`} />
                Split View
                {currentView === VIEW_SPLIT && (
                  <span className="ml-auto text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Active</span>
                )}
              </button>
            </div>

            {/* Actions */}
            <div className="p-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4 text-gray-500" />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserAccountMenu;
